/**
 * useInvoiceForm
 *
 * Composing hook for the invoice form. Delegates the heavy lifting to
 * focused sub-hooks:
 *
 *   - useInvoiceFormState       — useState/useRef declarations + reconstructed memos
 *   - useInvoiceFormDerived     — orgCountryCode, supplyType, party/address derivations
 *   - useInvoiceFormLineItems   — line item calculation, totals, tax breakdown, handlers
 *   - useInvoiceFormValidation  — validate() entry point
 *
 * Effect-driven side-effects live in useInvoiceFormEffects.ts.
 */

import type { Party, Address } from '@/gen/proto'

import {
  type LineItem,
  type InvoiceTotals,
} from './invoice-form-helpers'
import { useInvoiceFormDerived } from './useInvoiceFormDerived'
import { useInvoiceFormLineItems } from './useInvoiceFormLineItems'
import { useInvoiceFormState, type AddressSnapshot } from './useInvoiceFormState'
import { useInvoiceFormValidation } from './useInvoiceFormValidation'

import type { InvoiceFormProps } from './types'
import type { Account } from '../../../services/account-service'
import type { BankAccount } from '../../../services/bank-account-service'
import type { Invoice, InvoiceType, SupplyType } from '../../../types/billing'
import type { CustomerPurchaseOrder } from '../../../types/customer-purchase-order'

export type { AddressSnapshot } from './useInvoiceFormState'

export interface UseInvoiceFormReturn {
  // Settings
  dateFormat: string
  baseCurrency: string
  currencyOptions: Array<{ value: string; label: string }>

  // Core form state
  invoiceType: InvoiceType
  setInvoiceType: (t: InvoiceType) => void
  invoiceNumber: string
  setInvoiceNumber: (n: string) => void
  supplierBillNumber: string
  setSupplierBillNumber: (n: string) => void
  changeNotes: string
  setChangeNotes: (n: string) => void
  invoiceDate: Date | null
  setInvoiceDate: (d: Date | null) => void
  dueDate: Date | null
  setDueDate: (d: Date | null) => void
  selectedPartyId: string
  setSelectedPartyId: (id: string) => void
  partyRole: string
  setPartyRole: (r: string) => void
  roleOptions: Array<{ value: string; label: string }>
  setRoleOptions: (o: Array<{ value: string; label: string }>) => void
  currency: string
  setCurrency: (c: string) => void
  exchangeRate: string
  setExchangeRate: (r: string) => void
  lineItems: LineItem[]
  setLineItems: React.Dispatch<React.SetStateAction<LineItem[]>>
  notes: string
  setNotes: (n: string) => void
  termsConditions: string
  setTermsConditions: (t: string) => void
  paymentTerms: string
  setPaymentTerms: (t: string) => void
  selectedPaymentTermValue: string
  setSelectedPaymentTermValue: (v: string) => void
  customCreditDays: string
  setCustomCreditDays: (d: string) => void
  poNumber: string
  setPONumber: (n: string) => void
  partyVendorCode: string
  setPartyVendorCode: (c: string) => void
  isRoundOffEnabled: boolean
  setIsRoundOffEnabled: (e: boolean) => void
  placeOfSupply: string
  setPlaceOfSupply: (p: string) => void

  // Customer PO
  customerPOs: CustomerPurchaseOrder[]
  setCustomerPOs: (pos: CustomerPurchaseOrder[]) => void
  isCustomerPOSelectEnabled: boolean
  setIsCustomerPOSelectEnabled: (e: boolean) => void
  selectedCustomerPOId: string
  setSelectedCustomerPOId: (id: string) => void

  // Bank accounts
  bankAccounts: BankAccount[]
  setBankAccounts: (accounts: BankAccount[]) => void
  bankGLAccounts: Account[]
  setBankGLAccounts: (accounts: Account[]) => void
  selectedBankAccountId: string
  setSelectedBankAccountId: (id: string) => void

  // Walk-in / cash mode
  isWalkIn: boolean
  setIsWalkIn: (v: boolean) => void
  cashGLAccounts: Account[]
  setCashGLAccounts: (accounts: Account[]) => void
  selectedCashAccountId: string
  setSelectedCashAccountId: (id: string) => void

  // Credit limit
  partyOutstanding: number
  setPartyOutstanding: (n: number) => void
  creditLimitWarning: string | null
  setCreditLimitWarning: (w: string | null) => void

  // Export-specific
  exportType: 'goods' | 'services'
  setExportType: (t: 'goods' | 'services') => void
  lutNumber: string
  setLutNumber: (n: string) => void
  lutDate: Date | null
  setLutDate: (d: Date | null) => void

  // Address state
  shipToType: 'same' | 'party' | 'custom'
  setShipToType: (t: 'same' | 'party' | 'custom') => void
  customShipToAddress: AddressSnapshot
  consigneeType: 'same' | 'party' | 'custom'
  setConsigneeType: (t: 'same' | 'party' | 'custom') => void
  consigneeName: string
  customConsigneeAddress: AddressSnapshot
  notifyPartyType: 'same' | 'custom' | 'none'
  setNotifyPartyType: (t: 'same' | 'custom' | 'none') => void
  notifyPartyName: string
  customNotifyPartyAddress: AddressSnapshot

  // Selected address objects
  selectedShipToAddress: Address | null
  setSelectedShipToAddress: (a: Address | null) => void
  selectedConsigneeAddress: Address | null
  setSelectedConsigneeAddress: (a: Address | null) => void
  setSelectedNotifyPartyAddress: (a: Address | null) => void

  // Entity addresses
  entityAddresses: Address[]
  setEntityAddresses: (a: Address[]) => void

  // Modal state
  isShipToModalOpen: boolean
  setIsShipToModalOpen: (o: boolean) => void
  isConsigneeModalOpen: boolean
  setIsConsigneeModalOpen: (o: boolean) => void
  isQuickCustomerFormOpen: boolean
  setIsQuickCustomerFormOpen: (o: boolean) => void
  isBillingAddressSelectorOpen: boolean
  setIsBillingAddressSelectorOpen: (o: boolean) => void
  isShippingAddressSelectorOpen: boolean
  setIsShippingAddressSelectorOpen: (o: boolean) => void
  isVersionHistoryVisible: boolean
  setIsVersionHistoryVisible: (v: boolean) => void

  // Export shipping fields
  countryOfDestination: string
  setCountryOfDestination: (c: string) => void
  countryOfDestinationCode: string
  setCountryOfDestinationCode: (c: string) => void
  portOfLoading: string
  setPortOfLoading: (p: string) => void
  portOfDischarge: string
  setPortOfDischarge: (p: string) => void
  shippingBillNumber: string
  setShippingBillNumber: (n: string) => void
  shippingBillDate: Date | null
  setShippingBillDate: (d: Date | null) => void
  portCode: string
  setPortCode: (c: string) => void
  modeOfTransport: 'sea' | 'air' | 'road' | 'rail'
  setModeOfTransport: (m: 'sea' | 'air' | 'road' | 'rail') => void
  vesselFlightNumber: string
  setVesselFlightNumber: (n: string) => void
  blAwbNumber: string
  setBlAwbNumber: (n: string) => void
  containerNumbers: string
  setContainerNumbers: (n: string) => void

  // Submission state
  isSubmitting: boolean
  setIsSubmitting: (s: boolean) => void
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isErrorModalVisible: boolean
  setIsErrorModalVisible: (v: boolean) => void
  isFetchingRate: boolean
  setIsFetchingRate: (f: boolean) => void

  // Exchange rate ref (to track user-driven changes)
  currencyChangedByUser: React.MutableRefObject<boolean>

  // Derived values
  isEditing: boolean
  isEditingPosted: boolean
  orgCountryCode: string
  normalizedSellerStateCode: string
  selectedParty: Party | undefined
  supplyType: SupplyType
  isUrpPurchase: boolean
  totals: InvoiceTotals
  taxBreakdown: Array<[number, { cgst: number; sgst: number; igst: number }]>
  customerPOOptions: Array<{ value: string; label: string; description: string }>
  allPartyAddresses: Address[]
  orgAddresses: Address[]
  shippingAddressOptions: Address[]
  reconstructedPartyAddress: Address | null
  reconstructedShipToAddress: Address | null

  // Handlers
  calculateLineItem: (item: LineItem) => LineItem
  addLineItem: () => void
  removeLineItem: (id: string) => void
  updateLineItem: (id: string, field: keyof LineItem, value: string) => void
  handleCustomerPOSelection: (poId: string) => void
  validate: () => boolean
}

export function useInvoiceForm(
  initialData: Partial<Invoice> | undefined,
  parties: Party[],
  isVendor: boolean,
  sellerDetails: InvoiceFormProps['sellerDetails'],
): UseInvoiceFormReturn {
  const state = useInvoiceFormState({ initialData, isVendor })
  const derived = useInvoiceFormDerived({ state, parties, isVendor, sellerDetails })
  const lineItemHelpers = useInvoiceFormLineItems({ state, derived })
  const { validate } = useInvoiceFormValidation({ state, derived })

  return {
    ...state,
    ...derived,
    ...lineItemHelpers,
    validate,
  }
}
